import React, { useState } from 'react';
import { X, Phone, Mail, MapPin, PhoneCall, Send, Calendar } from 'lucide-react';
import { Borrower } from '../data/borrowers';
import { StatusBadge } from './StatusBadge';
import { EngagementTimeline } from './EngagementTimeline';
interface BorrowerPanelProps {
  borrower: Borrower | null;
  onClose: () => void;
  onSaveNote: (borrowerId: string, note: string) => void;
}
export function BorrowerPanel({
  borrower,
  onClose,
  onSaveNote
}: BorrowerPanelProps) {
  const [note, setNote] = useState('');
  if (!borrower) return null;
  const handleSaveNote = () => {
    if (note.trim()) {
      onSaveNote(borrower.id, note);
      setNote('');
    }
  };
  return <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} data-id="element-5" />
      <div className="fixed right-0 top-0 bottom-0 w-[480px] bg-white shadow-2xl z-50 overflow-y-auto" data-id="element-6">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-7">
          <div className="flex items-center gap-3" data-id="element-8">
            <div className="w-10 h-10 rounded-full bg-brand-navy-900 text-white flex items-center justify-center font-semibold" data-id="element-9">
              {borrower.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div data-id="element-10">
              <h2 className="text-lg font-bold text-brand-navy-900" data-id="element-11">
                {borrower.name}
              </h2>
              <StatusBadge status={borrower.status} data-id="element-12" />
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-13">
            <X className="w-5 h-5 text-slate-600" data-id="element-14" />
          </button>
        </div>

        <div className="p-6 space-y-6" data-id="element-15">
          <div className="flex gap-2" data-id="element-16">
            <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-orange-500 text-white rounded-lg hover:bg-brand-orange-600 transition-colors" data-id="element-17">
              <PhoneCall className="w-4 h-4" data-id="element-18" />
              Call
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-19">
              <Send className="w-4 h-4" data-id="element-20" />
              Email
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-21">
              <Calendar className="w-4 h-4" data-id="element-22" />
              Schedule
            </button>
          </div>

          <div data-id="element-23">
            <h3 className="text-sm font-semibold text-brand-navy-900 uppercase tracking-wide mb-3" data-id="element-24">
              Borrower Snapshot
            </h3>
            <div className="space-y-2 bg-slate-50 rounded-lg p-4" data-id="element-25">
              <div className="flex items-center gap-2 text-sm" data-id="element-26">
                <Phone className="w-4 h-4 text-slate-500" data-id="element-27" />
                <span className="text-slate-900" data-id="element-28">{borrower.phone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-29">
                <Mail className="w-4 h-4 text-slate-500" data-id="element-30" />
                <span className="text-slate-900" data-id="element-31">{borrower.email}</span>
              </div>
              <div className="flex items-start gap-2 text-sm" data-id="element-32">
                <MapPin className="w-4 h-4 text-slate-500 mt-0.5" data-id="element-33" />
                <span className="text-slate-900" data-id="element-34">{borrower.address}</span>
              </div>
            </div>
          </div>

          <div data-id="element-35">
            <h3 className="text-sm font-semibold text-brand-navy-900 uppercase tracking-wide mb-3" data-id="element-36">
              Most Recent Loan
            </h3>
            <div className="bg-slate-50 rounded-lg p-4" data-id="element-37">
              <div className="grid grid-cols-2 gap-4" data-id="element-38">
                <div data-id="element-39">
                  <div className="text-xs text-slate-600 mb-1" data-id="element-40">Loan Amount</div>
                  <div className="text-2xl font-bold text-brand-navy-900 tabular-nums" data-id="element-41">
                    ${borrower.loanAmount.toLocaleString()}
                  </div>
                </div>
                <div data-id="element-42">
                  <div className="text-xs text-slate-600 mb-1" data-id="element-43">
                    Interest Rate
                  </div>
                  <div className="text-2xl font-bold text-brand-navy-900 tabular-nums" data-id="element-44">
                    {borrower.interestRate.toFixed(3)}%
                  </div>
                </div>
                <div data-id="element-45">
                  <div className="text-xs text-slate-600 mb-1" data-id="element-46">Loan Type</div>
                  <div className="text-sm font-medium text-slate-900" data-id="element-47">
                    {borrower.loanType}
                  </div>
                </div>
                <div data-id="element-48">
                  <div className="text-xs text-slate-600 mb-1" data-id="element-49">Funded Date</div>
                  <div className="text-sm font-medium text-slate-900" data-id="element-50">
                    {new Date(borrower.fundedDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  </div>
                </div>
                <div data-id="element-51">
                  <div className="text-xs text-slate-600 mb-1" data-id="element-52">Loan Number</div>
                  <div className="text-sm font-mono text-slate-900" data-id="element-53">
                    {borrower.loanNumber}
                  </div>
                </div>
                <div data-id="element-54">
                  <div className="text-xs text-slate-600 mb-1" data-id="element-55">LOA</div>
                  <div className="text-sm font-medium text-slate-900" data-id="element-56">
                    {borrower.loa}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div data-id="element-57">
            <h3 className="text-sm font-semibold text-brand-navy-900 uppercase tracking-wide mb-3" data-id="element-58">
              Engagement History
            </h3>
            <EngagementTimeline entries={borrower.engagementHistory} data-id="element-59" />
          </div>

          <div data-id="element-60">
            <h3 className="text-sm font-semibold text-brand-navy-900 uppercase tracking-wide mb-3" data-id="element-61">
              Quick Note Entry
            </h3>
            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Document this conversation..." className="w-full h-32 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange-500 resize-none" data-id="element-62" />
            <button onClick={handleSaveNote} className="mt-3 w-full px-4 py-2 bg-brand-orange-500 text-white font-medium rounded-lg hover:bg-brand-orange-600 transition-colors" data-id="element-63">
              Save Note
            </button>
          </div>
        </div>
      </div>
    </>;
}